import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Tilt from "react-parallax-tilt";
import {
  Trophy,
  Calendar,
  Award,
  Users,
  ArrowRight,
  ArrowUpRight,
  Medal,
  MapPin,
  ChevronDown
} from "lucide-react";
import { useTheme } from "../context/ThemeContext";
import heroVideo from "../assets/hero-golf2.mp4";
import RotatingText from "../components/common/RotatingText";
import WhatsAppSection from "../components/sections/WhatsAppSection";
import ResultsGallery from "../components/sections/ResultsGallery";
import FadeInSection from "../components/common/FadeInSection";
import AmbientBackground from "../components/common/AmbientBackground";
import TournamentTicker from "../components/sections/TournamentTicker";
import StatementBlock from "../components/sections/StatementBlock";
import BentoStats from "../components/sections/BentoStats";
import FeaturedChampion from "../components/sections/FeaturedChampion";
import ParallaxQuote from "../components/sections/ParallaxQuote";
import ClubsMap from "../components/sections/ClubsMap";
import LatestNews from "../components/sections/LatestNews";
import { getUpcomingTournaments } from "../services/tournamentService";

const HomePage = () => {
  const { isDarkMode } = useTheme();
  const [isLoaded, setIsLoaded] = useState(false);
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { scrollY } = useScroll();
  const heroOpacity = useTransform(scrollY, [0, 600], [1, 0.3]);
  const heroScale = useTransform(scrollY, [0, 600], [1, 1.1]);
  const heroTextY = useTransform(scrollY, [0, 400], [0, 100]);

  useEffect(() => {
    setIsLoaded(true);
    loadTournaments();
  }, []);

  const loadTournaments = async () => {
    try {
      setLoading(true);
      const response = await getUpcomingTournaments({ limit: 3 });
      if (response.success && response.data) {
        const sorted = response.data
          .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
          .slice(0, 3);
        setTournaments(sorted);
      }
    } catch (err) {
      console.error("Error loading tournaments:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("en-ZA", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric"
    });

  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (start.toDateString() === end.toDateString()) return formatDate(startDate);
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  };

  const getCapacityInfo = (tournament) => {
    const max = tournament.field_size || tournament.max_players || 0;
    const current =
      tournament.registered_count ||
      tournament.registrations_count ||
      tournament.draw_count ||
      0;
    if (!max) return null;
    const percent = Math.min(100, Math.round((current / max) * 100));
    const spotsLeft = Math.max(0, max - current);
    return { max, current, percent, spotsLeft };
  };

  // Quick links - golf-appropriate palette: greens, amber/gold, blue, slate
  // No pink, no purple
  const quickLinks = [
    {
      title: "Tournaments",
      description: "Upcoming competitions",
      icon: Trophy,
      href: "/tournaments",
      gradient: "from-emerald-400 to-emerald-600",
      glow: "rgba(16, 185, 129, 0.4)"
    },
    {
      title: "Results",
      description: "Latest scores & reports",
      icon: Award,
      href: "/results",
      gradient: "from-blue-400 to-blue-600",
      glow: "rgba(59, 130, 246, 0.4)"
    },
    {
      title: "Order of Merit",
      description: "Player rankings",
      icon: Medal,
      href: "/order-of-merit",
      gradient: "from-amber-400 to-amber-600",
      glow: "rgba(251, 191, 36, 0.4)"
    },
    {
      title: "Fixtures",
      description: "Full schedule",
      icon: Calendar,
      href: "/fixtures",
      gradient: "from-slate-500 to-slate-700",
      glow: "rgba(100, 116, 139, 0.4)"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* ============================================================ */}
      {/* Hero */}
      {/* ============================================================ */}
      <section className="relative overflow-hidden min-h-screen flex items-center justify-center">
        <motion.div
          className="absolute inset-0"
          style={{ opacity: heroOpacity, scale: heroScale }}
        >
          <video autoPlay loop muted playsInline className="w-full h-full object-cover">
            <source src={heroVideo} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/40" />
        </motion.div>

        <motion.div
          className="relative z-10 text-center px-4 -mt-16 sm:-mt-24 md:-mt-32 lg:-mt-48"
          style={{ y: heroTextY }}
        >
          <div
            className={`transition-all duration-500 mb-20 sm:mb-28 md:mb-40 lg:mb-48 ${
              isLoaded ? "opacity-100 scale-100" : "opacity-0 scale-95"
            }`}
          >
            <h1
              className="hidden lg:block text-6xl xl:text-7xl font-bold font-cursive drop-shadow-lg"
              style={{ color: "#000000" }}
            >
              Home of the Free State Golf Union
            </h1>
            <div className="lg:hidden">
              <h1
                className="text-4xl sm:text-5xl md:text-6xl font-bold font-cursive drop-shadow-lg mb-2 sm:mb-3"
                style={{ color: "#000000" }}
              >
                Home of the
              </h1>
              <h2
                className="text-3xl sm:text-4xl md:text-5xl font-bold font-cursive drop-shadow-lg"
                style={{ color: "#000000" }}
              >
                Free State Golf Union
              </h2>
            </div>
          </div>

          <div
            className={`flex flex-wrap items-center justify-center gap-2 sm:gap-3 text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-light transition-all duration-700 delay-200 ${
              isLoaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <span className="drop-shadow-lg text-white">Promoting</span>
            <RotatingText
              texts={["Excellence", "Integrity", "Sportsmanship", "Community", "Growth"]}
              mainClassName={`font-cursive font-normal px-4 sm:px-6 py-2 sm:py-3 pb-3 sm:pb-4 rounded-xl sm:rounded-2xl ${
                isDarkMode ? "bg-fsgu-green-700/95 text-white" : "bg-fsgu-green-700 text-white"
              } drop-shadow-2xl`}
              splitLevelClassName="overflow-visible"
              staggerFrom="first"
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: "-120%", opacity: 0 }}
              staggerDuration={0.03}
              transition={{ type: "spring", damping: 30, stiffness: 400 }}
              rotationInterval={3000}
            />
          </div>
        </motion.div>

        <TournamentTicker />

        <motion.div
          className="absolute bottom-20 sm:bottom-24 left-1/2 -translate-x-1/2 z-10 hidden sm:block"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6 text-white/70 drop-shadow-lg" />
        </motion.div>
      </section>

      <StatementBlock />
      <BentoStats />

      {/* ============================================================ */}
      {/* Quick Access - liquid glass cards, golf palette */}
      {/* ============================================================ */}
      <section className="py-16 sm:py-20 lg:py-28 relative overflow-hidden">
        <AmbientBackground variant="navy" />

        <div className="container-custom relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10 sm:mb-14"
          >
            <div className="inline-flex items-center justify-center gap-2 mb-4">
              <div className={`h-px w-8 ${isDarkMode ? 'bg-emerald-400/60' : 'bg-emerald-500/60'}`} />
              <span className={`text-[10px] sm:text-xs font-semibold uppercase tracking-[0.3em] ${
                isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
              }`}>
                Explore
              </span>
              <div className={`h-px w-8 ${isDarkMode ? 'bg-emerald-400/60' : 'bg-emerald-500/60'}`} />
            </div>
            <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold font-cursive mb-3 ${
              isDarkMode ? 'text-white' : 'text-slate-900'
            }`}>
              Quick Access
            </h2>
            <p className={`text-base sm:text-lg ${
              isDarkMode ? 'text-slate-400' : 'text-slate-600'
            }`}>
              Everything you need at your fingertips
            </p>
          </motion.div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5 max-w-5xl mx-auto">
            {quickLinks.map((link, index) => {
              const IconComponent = link.icon;
              const tiltEnabled = typeof window !== 'undefined' && window.innerWidth >= 768;

              return (
                <motion.div
                  key={link.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.1 }}
                  transition={{
                    duration: 0.6,
                    delay: index * 0.08,
                    ease: [0.22, 1, 0.36, 1]
                  }}
                  className="relative group"
                >
                  {/* Hover glow behind */}
                  <div
                    className="absolute -inset-1 rounded-3xl blur-xl opacity-0 group-hover:opacity-70 transition-opacity duration-500"
                    style={{ background: link.glow }}
                  />

                  <Tilt
                    tiltMaxAngleX={8}
                    tiltMaxAngleY={8}
                    perspective={1400}
                    transitionSpeed={1500}
                    scale={1.02}
                    tiltEnable={tiltEnabled}
                    glareEnable={true}
                    glareMaxOpacity={0.15}
                    glareColor="#ffffff"
                    glarePosition="top"
                  >
                    <Link
                      to={link.href}
                      className={`relative block h-full p-5 sm:p-6 rounded-2xl sm:rounded-3xl overflow-hidden backdrop-blur-xl border transition-all duration-500 ${
                        isDarkMode
                          ? 'bg-white/[0.04] border-white/[0.08] group-hover:border-white/20'
                          : 'bg-white/70 border-white/80 group-hover:border-white/100'
                      }`}
                    >
                      {/* Top shine */}
                      <div
                        className="absolute inset-x-0 top-0 h-px"
                        style={{
                          background: isDarkMode
                            ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)'
                            : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.95), transparent)'
                        }}
                      />

                      {/* Inner glow */}
                      <div
                        className="absolute inset-0 opacity-40 pointer-events-none"
                        style={{
                          background: isDarkMode
                            ? 'radial-gradient(circle at 50% 0%, rgba(255,255,255,0.06), transparent 60%)'
                            : 'radial-gradient(circle at 50% 0%, rgba(255,255,255,0.7), transparent 60%)'
                        }}
                      />

                      {/* Gradient tint that appears on hover */}
                      <div
                        className={`absolute inset-0 bg-gradient-to-br ${link.gradient} opacity-0 group-hover:opacity-[0.08] transition-opacity duration-500`}
                      />

                      <div className="relative z-10">
                        {/* Icon with gradient */}
                        <div
                          className={`inline-flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl mb-4 sm:mb-5 bg-gradient-to-br ${link.gradient} shadow-lg group-hover:scale-110 transition-transform duration-500`}
                          style={{
                            boxShadow: `0 10px 30px -5px ${link.glow}`
                          }}
                        >
                          <IconComponent className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                        </div>

                        <h3 className={`text-base sm:text-lg font-bold mb-1 sm:mb-1.5 ${
                          isDarkMode ? 'text-white' : 'text-slate-900'
                        }`}>
                          {link.title}
                        </h3>

                        <p className={`text-xs sm:text-sm mb-4 ${
                          isDarkMode ? 'text-slate-400' : 'text-slate-600'
                        }`}>
                          {link.description}
                        </p>

                        <div className={`inline-flex items-center text-xs sm:text-sm font-semibold gap-1 group-hover:gap-2 transition-all ${
                          isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                        }`}>
                          Open
                          <ArrowUpRight className="w-3.5 h-3.5 sm:w-4 sm:h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        </div>
                      </div>
                    </Link>
                  </Tilt>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/* Upcoming Tournaments - glass cards, centered mobile */}
      {/* ============================================================ */}
      <section className="py-16 sm:py-20 lg:py-28 relative overflow-hidden">
        <AmbientBackground variant="green" />

        <div className="container-custom relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-10 sm:mb-14"
          >
            <div className="inline-flex items-center justify-center gap-2 mb-4">
              <div className={`h-px w-8 ${isDarkMode ? 'bg-emerald-400/60' : 'bg-emerald-500/60'}`} />
              <span className={`text-[10px] sm:text-xs font-semibold uppercase tracking-[0.3em] ${
                isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
              }`}>
                Upcoming
              </span>
              <div className={`h-px w-8 ${isDarkMode ? 'bg-emerald-400/60' : 'bg-emerald-500/60'}`} />
            </div>
            <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-bold font-cursive mb-3 ${
              isDarkMode ? 'text-white' : 'text-slate-900'
            }`}>
              Upcoming Tournaments
            </h2>
            <p className={`text-base sm:text-lg mb-6 ${
              isDarkMode ? 'text-slate-400' : 'text-slate-600'
            }`}>
              Register for the next round of competitions
            </p>
            <Link
              to="/tournaments"
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold backdrop-blur-xl border transition-all duration-300 hover:scale-[1.02] ${
                isDarkMode
                  ? 'bg-white/[0.05] border-white/10 text-emerald-400 hover:bg-white/[0.08]'
                  : 'bg-white/60 border-white/80 text-emerald-600 hover:bg-white/80'
              }`}
            >
              <Trophy className="w-4 h-4" />
              View all tournaments
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </motion.div>

          {loading ? (
            <div className="text-center py-12">
              <div className={`inline-block animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-4 border-solid border-current border-r-transparent ${
                isDarkMode ? 'text-emerald-400' : 'text-emerald-500'
              }`} />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className={isDarkMode ? 'text-red-400' : 'text-red-500'}>
                Error loading tournaments: {error}
              </p>
            </div>
          ) : tournaments.length === 0 ? (
            <div className="text-center py-12">
              <p className={isDarkMode ? 'text-slate-400' : 'text-slate-600'}>
                No upcoming tournaments at the moment. Check back soon!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 max-w-md md:max-w-none mx-auto">
              {tournaments.map((tournament, index) => {
                const capacity = getCapacityInfo(tournament);
                const tiltEnabled = typeof window !== "undefined" && window.innerWidth >= 768;

                return (
                  <motion.div
                    key={tournament.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.1 }}
                    transition={{
                      duration: 0.6,
                      delay: index * 0.1,
                      ease: [0.22, 1, 0.36, 1]
                    }}
                    className="relative group"
                  >
                    {/* Hover glow */}
                    <div className="absolute -inset-1 rounded-3xl bg-gradient-to-br from-emerald-500/20 to-amber-500/10 blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                    <Tilt
                      tiltMaxAngleX={5}
                      tiltMaxAngleY={5}
                      perspective={1500}
                      transitionSpeed={1500}
                      scale={1.015}
                      tiltEnable={tiltEnabled}
                    >
                      <Link
                        to={`/tournaments/${tournament.id}`}
                        className={`relative block rounded-2xl sm:rounded-3xl overflow-hidden backdrop-blur-xl border transition-all duration-500 h-full ${
                          isDarkMode
                            ? 'bg-white/[0.04] border-white/[0.08] group-hover:border-emerald-400/30'
                            : 'bg-white/70 border-white/80 group-hover:border-emerald-400/50 shadow-lg'
                        }`}
                      >
                        <div
                          className="absolute inset-x-0 top-0 h-px z-10"
                          style={{
                            background: isDarkMode
                              ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)'
                              : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.95), transparent)'
                          }}
                        />

                        {tournament.poster_image_url ? (
                          <div className="relative h-44 sm:h-52 overflow-hidden">
                            <img
                              src={tournament.poster_image_url}
                              alt={tournament.name || tournament.tournament_name}
                              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                            <div className="absolute top-3 left-3">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-semibold backdrop-blur-xl border ${
                                tournament.status === "Open" || tournament.status === "open"
                                  ? 'bg-emerald-500/90 text-white border-white/20'
                                  : tournament.status === "Closed" || tournament.status === "closed"
                                  ? 'bg-red-500/90 text-white border-white/20'
                                  : 'bg-amber-500/90 text-white border-white/20'
                              }`}>
                                {tournament.status}
                              </span>
                            </div>

                            {capacity && capacity.spotsLeft > 0 && capacity.spotsLeft <= 20 && (
                              <div className="absolute top-3 right-3">
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-semibold backdrop-blur-xl bg-amber-500/90 text-white border border-white/20">
                                  <span className="relative flex h-1.5 w-1.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-white"></span>
                                  </span>
                                  {capacity.spotsLeft} left
                                </span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="relative h-44 sm:h-52 flex items-center justify-center bg-gradient-to-br from-emerald-500/10 to-amber-500/5">
                            <Trophy className={`w-12 h-12 ${
                              isDarkMode ? 'text-emerald-500/40' : 'text-emerald-500/50'
                            }`} />
                          </div>
                        )}

                        <div className="p-5 sm:p-6">
                          {tournament.category && (
                            <div className="mb-3">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-medium ${
                                isDarkMode
                                  ? 'bg-white/[0.06] text-slate-300 border border-white/10'
                                  : 'bg-slate-900/[0.05] text-slate-700 border border-slate-900/10'
                              }`}>
                                {tournament.category}
                              </span>
                            </div>
                          )}

                          <h3 className={`text-lg sm:text-xl font-bold mb-2 sm:mb-3 group-hover:text-emerald-500 transition-colors ${
                            isDarkMode ? 'text-white' : 'text-slate-900'
                          }`}>
                            {tournament.name || tournament.tournament_name}
                          </h3>

                          {tournament.description && (
                            <p className={`text-xs sm:text-sm mb-4 line-clamp-2 leading-relaxed ${
                              isDarkMode ? 'text-slate-400' : 'text-slate-600'
                            }`}>
                              {tournament.description}
                            </p>
                          )}

                          <div className="space-y-2 mb-4">
                            <div className={`flex items-center text-xs sm:text-sm ${
                              isDarkMode ? 'text-slate-300' : 'text-slate-700'
                            }`}>
                              <Calendar className={`w-4 h-4 mr-2 flex-shrink-0 ${
                                isDarkMode ? 'text-emerald-400' : 'text-emerald-500'
                              }`} />
                              <span className="truncate">
                                {formatDateRange(tournament.start_date, tournament.end_date)}
                              </span>
                            </div>

                            {(tournament.venue || tournament.venue_name) && (
                              <div className={`flex items-center text-xs sm:text-sm ${
                                isDarkMode ? 'text-slate-300' : 'text-slate-700'
                              }`}>
                                <MapPin className={`w-4 h-4 mr-2 flex-shrink-0 ${
                                  isDarkMode ? 'text-amber-400' : 'text-amber-500'
                                }`} />
                                <span className="truncate">
                                  {tournament.venue || tournament.venue_name}
                                  {tournament.venue_city && `, ${tournament.venue_city}`}
                                </span>
                              </div>
                            )}
                          </div>

                          {capacity && (
                            <div className="mb-4">
                              <div className={`flex items-center justify-between text-[10px] sm:text-xs mb-1.5 ${
                                isDarkMode ? 'text-slate-400' : 'text-slate-600'
                              }`}>
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {capacity.current} / {capacity.max}
                                </span>
                                <span className="font-semibold">
                                  {capacity.percent}%
                                </span>
                              </div>
                              <div className={`h-1.5 rounded-full overflow-hidden ${
                                isDarkMode ? 'bg-white/[0.06]' : 'bg-slate-900/[0.06]'
                              }`}>
                                <motion.div
                                  initial={{ width: 0 }}
                                  whileInView={{ width: `${capacity.percent}%` }}
                                  viewport={{ once: true }}
                                  transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
                                  className={`h-full rounded-full ${
                                    capacity.percent >= 90
                                      ? 'bg-gradient-to-r from-red-500 to-orange-500'
                                      : capacity.percent >= 70
                                      ? 'bg-gradient-to-r from-amber-400 to-amber-600'
                                      : 'bg-gradient-to-r from-emerald-400 to-emerald-600'
                                  }`}
                                />
                              </div>
                            </div>
                          )}

                          <div className={`pt-4 border-t ${
                            isDarkMode ? 'border-white/[0.08]' : 'border-slate-900/[0.06]'
                          }`}>
                            <span className={`text-xs sm:text-sm font-semibold group-hover:text-emerald-500 transition-colors inline-flex items-center gap-1 group-hover:gap-2 ${
                              isDarkMode ? 'text-emerald-400' : 'text-emerald-600'
                            }`}>
                              View Details
                              <ArrowUpRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </span>
                          </div>
                        </div>
                      </Link>
                    </Tilt>
                  </motion.div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <FeaturedChampion />
      <ParallaxQuote />
      <ClubsMap />

      <FadeInSection>
        <ResultsGallery />
      </FadeInSection>

      <FadeInSection>
        <WhatsAppSection />
      </FadeInSection>

      <LatestNews />
    </div>
  );
};

export default HomePage;